import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
// Actions
import {
  SG_GET_TASK_SCHEDULES,
  SG_GET_TASK_TEMPLATES,
  SG_GET_TASK_TEMPLATE_SELECT,
} from "constants/actions";

// Pages
import SurveyLibrary from "./SurveyLibrary";
import SurveyFormPage from "./SurveyFormPage";
import PulseBuilderPage from "./PulseBuilderPage";
import PulseDataViewPage from "./PulseDataViewPage";
import PulseScheduleSurvey from "./PulseScheduleSurvey";
import { PulseSurveysProvider } from "./Data/LandingPageContext";
import ManagePulse from "ProtoType/PulseAnalyticsV2/ManagePulse";
import { SurveyDataProvider } from "./Data/SurveyViewContext";

// Import providers for schedule survey
import { SurveyProvider } from "./Data/SurveyFormContext";
import { UserSearchProvider } from "Providers/UserSearchProvider";
import ScheduleBreadcrumb from "../../ScheduleV2/ScheduleBreadcrumb";

const PulseSurveyContent = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [currentView, setCurrentView] = useState();
  const [breadcrumbCrumbs, setBreadcrumbCrumbs] = useState([
    { label: "Pulse Surveys" },
  ]);

  // Define navigateToForm function
  const navigateToForm = useCallback((survey) => {
    navigate("/leader/audit/schedule-v2/pulse/create", {
      state: { initialSurveyData: survey }, // survey contains only serializable fields
    });
  }, [navigate]);

  const { scheduledPulseSurveys, allPulseSurveys, get_auth, pulseFetched } =
    useSelector(
      (state) => ({
        scheduledPulseSurveys: state.task_templates.scheduled_tasks,
        allPulseSurveys: state.task_templates.task_templates,
        get_auth: state.auth,
        pulseFetched: state.task_templates.surveysFetched,
      }),
      shallowEqual
    );

  useEffect(() => {
    dispatch({ type: SG_GET_TASK_TEMPLATE_SELECT });
    dispatch({ type: SG_GET_TASK_SCHEDULES });
    dispatch({ type: SG_GET_TASK_TEMPLATES });
  }, [dispatch]);

  useEffect(() => {
    // Simplified routing - handle create, edit, view, builder, schedule, and manage
    const pathList = ["create", "edit", "view", "builder", "schedule", "manage"];
    const regex = new RegExp(
      `^/leader/audit/schedule-v2/pulse/(?:(${pathList.join(
        "|"
      )})(?:/(\\d+))?)?$`
    );

    const match = location.pathname.match(regex);

    if (match) {
      const operation = match[1]; // "create", "edit", "view", or "builder"
      const id = match[2]; // ID if present

      switch (operation) {
        case "builder":
          setCurrentView(<PulseBuilderPage navigateToForm={navigateToForm} />);
          setBreadcrumbCrumbs([{ label: "Pulse Builder" }]);
          break;

        case "create":
          setCurrentView(<SurveyFormPage mode="create" />);
          setBreadcrumbCrumbs([{ label: "Create Survey" }]);
          break;

        case "edit":
          setCurrentView(<SurveyFormPage mode="edit" surveyId={id} />);
          setBreadcrumbCrumbs([{ label: "Edit Survey" }]);
          break;

        case "view":
          setCurrentView(<PulseDataViewPage surveyId={id} />);
          setBreadcrumbCrumbs([]);
          break;

        case "schedule":
          setCurrentView(
            <UserSearchProvider>
              <SurveyProvider 
                initialFormData={location.state?.initialSurveyData}
                mode="create"
              >
                <PulseScheduleSurvey />
              </SurveyProvider>
            </UserSearchProvider>
          );
          setBreadcrumbCrumbs([{ label: "Schedule Survey" }]);
          break;

        case "manage":
          setCurrentView(
            <SurveyDataProvider>
              <ManagePulse
                surveyId={id}
                onBreadcrumbChange={setBreadcrumbCrumbs}
              />
            </SurveyDataProvider>
          );
          break;

        default:
          // Default to survey library
          setCurrentView(<SurveyLibrary navigateToForm={navigateToForm} />);
          setBreadcrumbCrumbs([{ label: "Pulse Surveys" }]);
          break;
      }
    } else {
      // Default to survey library for base route
      setCurrentView(<SurveyLibrary navigateToForm={navigateToForm} />);
      setBreadcrumbCrumbs([{ label: "Pulse Surveys" }]);
    }
  }, [location.pathname, scheduledPulseSurveys, allPulseSurveys, pulseFetched, navigateToForm, location.state]);

  if (!currentView) {
    return null;
  }

  return (
    <>
      {breadcrumbCrumbs.length > 0 && <ScheduleBreadcrumb crumbs={breadcrumbCrumbs} />}
      {currentView}
    </>
  );
};

const PulseSurvey = () => {
  return (
    <PulseSurveysProvider>
      <PulseSurveyContent />
    </PulseSurveysProvider>
  );
};

export default PulseSurvey;
